import OTPInput from "components/OTPInput";
import { useAuth } from "context/AuthContextProvider";
import { useVerifyAccount } from "hooks/mutations/auth";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { AuthCard, AuthHeading, AuthSub } from "./AuthUi";
import { formatPhone } from "shared/utils/phone";
import { textPx } from "shared/constants/zoom";

/** OTP verification — centered card with the 6-box input; logic unchanged. */
const VerifyOTP = () => {
  const verify_number = sessionStorage.getItem("verify_number");
  const reset_phone = sessionStorage.getItem("reset_number");
  // Two flows share this page: an unverified account's first login (useLogin
  // stores verify_number) and forgot-password (ForgetPassword stores
  // reset_number). Back, and the no-confirmation guard below, used to send
  // BOTH to forgot-password, so a first-time admin who pressed Kembali (or
  // reloaded) landed on a page they never started from.
  const cameFromLogin = !!verify_number;
  const backPath = cameFromLogin ? PATHS.LOGIN : PATHS.FORGET_PASSWORD;
  const onBack = () => {
    if (cameFromLogin) sessionStorage.removeItem("verify_number");
    navigate(backPath);
  };

  const navigate = useNavigate();
  const { confirmOtp, confirmationResult, getPhoneProof } = useAuth();
  const { mutateAsync: verifyAccount } = useVerifyAccount();
  // confirmOtp clears confirmationResult; the guard below must not fire then.
  const confirming = useRef(false);

  const onComplete = async (otp: string) => {
    confirming.current = true;
    try {
      await confirmOtp(otp);
    } catch (err) {
      confirming.current = false;
      throw err;
    }
    if (verify_number) {
      const firebase_id_token = await getPhoneProof();
      if (!firebase_id_token) throw Error("No phone sign-in after OTP confirm");
      // The hook toasts + redirects on error; don't show it as a wrong OTP.
      await verifyAccount({ phone_number: verify_number, firebase_id_token }).catch(() => undefined);
    } else {
      navigate(PATHS.RESET_PASSWORD);
    }
  };

  useEffect(() => {
    if (!confirmationResult && !confirming.current) {
      navigate(backPath);
    }
    // eslint-disable-next-line
  }, [confirmationResult]);

  return (
    <AuthCard onBack={onBack}>
      <AuthHeading>{translate("title.verify_otp")}</AuthHeading>
      <AuthSub marginBottom={26}>
        {translate("subtitle.otp")}{" "}
        <b style={{ color: colors.ink, fontWeight: 600 }}>{formatPhone(verify_number || reset_phone)}.</b>
      </AuthSub>

      <OTPInput onComplete={onComplete} />

      <div style={{ textAlign: "center", marginTop: 22 }}>
        <div style={{ fontSize: textPx(13), fontWeight: 600, color: colors.ink2 }}>
          {translate("subtitle.otp_not_received")}
        </div>
        <div style={{ fontSize: textPx(12), color: colors.ink3, marginTop: 6, lineHeight: 1.55 }}>
          {translate("subtitle.otp_2")}
        </div>
      </div>
    </AuthCard>
  );
};

export default VerifyOTP;
